import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { db } from '@/lib/db';
import { courseHcpForPlayer } from '@/lib/games/engine/helpers';
import type { Member, MemberPublic, HoleRange } from '@/lib/db/types';
import { CourseTabPicker } from '@/components/shared/CourseTabPicker';
import { RoundLengthPicker, holeRangeFromLength, type RoundLength } from '@/components/shared/RoundLengthPicker';
import { GameSetupMemberSelect, type NonMemberPlayer } from '@/components/games/GameSetupMemberSelect';
import { CategorizedGamePicker, defaultEnabledGames } from './CategorizedGamePicker';
import { StakesScoringPanel, buildInitialStakes } from './StakesScoringPanel';
import { defaultStakeForFormat, showWolfPointMultipliers } from '@/lib/games/engine/stakes-helpers';
import { DEFAULT_WOLF_ROUND_CONFIG } from '@/lib/games/formats/wolf';
import type { WolfRoundConfig } from '@/lib/games/engine/types';
import { OpenToJoinersCard } from '@/components/shared/OpenToJoinersCard';
import { WolfSetupPanel } from './WolfSetupPanel';
import { autoTeams, enabledGamesNeedTeams, stakesToGameRows } from './useRoundEngine';
import { buildTeamsFromSetup, RoundTeamsSetupPanel } from './RoundTeamsSetupPanel';
import { playerKeyFor } from '@/lib/db/group-rounds';
import type { StakesConfig, NassauMatchConfig } from '@/lib/games/engine/types';

interface Props {
  member: Member;
  onClose: () => void;
  onStarted: (roundId: string) => void;
}

export function CreateRoundWizard({ member, onClose, onStarted }: Props) {
  const [courses, setCourses] = useState<Awaited<ReturnType<typeof db.courses.list>>>([]);
  const [members, setMembers] = useState<MemberPublic[]>([]);
  const [following, setFollowing] = useState<string[]>([]);
  const [courseId, setCourseId] = useState('');
  const [teeId, setTeeId] = useState('');
  const [playedAt, setPlayedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [participants, setParticipants] = useState<string[]>([]);
  const [guests, setGuests] = useState<NonMemberPlayer[]>([]);
  const [roundLength, setRoundLength] = useState<RoundLength>(18);
  const [nineSide, setNineSide] = useState<'front-9' | 'back-9'>('front-9');
  const [isJoinable, setIsJoinable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capMessage, setCapMessage] = useState<string | null>(null);
  const [wizardStep, setWizardStep] = useState<'setup' | 'games' | 'teams'>('setup');
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => defaultEnabledGames());
  const [stakes, setStakes] = useState<StakesConfig>(() => buildInitialStakes([]));
  const [wolfConfig, setWolfConfig] = useState<WolfRoundConfig>(() => ({ ...DEFAULT_WOLF_ROUND_CONFIG }));
  const [skipGames, setSkipGames] = useState(false);
  const [teamMode, setTeamMode] = useState<'auto' | 'manual'>('auto');
  const [manualTeams, setManualTeams] = useState<Record<string, number>>({});
  const [matchOpponents, setMatchOpponents] = useState<Record<string, string>>({});
  const [nassauMatches, setNassauMatches] = useState<Record<string, NassauMatchConfig[]>>({});

  useEffect(() => {
    void Promise.all([
      db.courses.list(),
      db.members.list({ limit: 100 }),
      db.follows.following(),
    ]).then(([c, m, f]) => {
      setCourses(c);
      setMembers(m.filter((x) => x.id !== member.id));
      setFollowing(f);
      setCourseId(c[0]?.id || '');
    });
  }, [member.id]);

  useEffect(() => {
    if (!courseId) return;
    void db.courses.tees(courseId).then((tees) => {
      const white = tees.find((t) => t.name.toLowerCase().includes('white')) || tees[0];
      if (white) setTeeId(white.id);
    });
  }, [courseId]);

  const guestCount = guests.length;
  const invitedCount = participants.length;
  const totalPlayers = 1 + invitedCount + guestCount;
  const atCap = totalPlayers >= 4;
  const holes: HoleRange = holeRangeFromLength(roundLength, nineSide);

  const roster = useMemo(() => {
    const self = { id: member.id, name: `${member.name} (you)`, member_id: member.id as string };
    const invited = members.filter((m) => participants.includes(m.id)).map((m) => ({
      id: m.id, name: m.name, member_id: m.id,
    }));
    const guestPlayers = guests.map((g, i) => ({
      id: `guest_${i}`, name: g.name.trim(), member_id: null as string | null, guestIdx: i,
    }));
    return [self, ...invited, ...guestPlayers];
  }, [member, members, participants, guests]);

  const handleCapHit = () => {
    setCapMessage('That\'s a full foursome — 4 players max.');
    setTimeout(() => setCapMessage(null), 3000);
  };

  const parseGuestHandicap = (raw: string): number | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : null;
  };

  const enabledIds = useMemo(() => Object.entries(enabled).filter(([, v]) => v).map(([k]) => k), [enabled]);
  const orgKey = useMemo(() => playerKeyFor(member.id), [member.id]);
  const needsTeams = !skipGames && enabledIds.length > 0 && enabledGamesNeedTeams(enabledIds);

  const stakesRoster = useMemo(() => roster.map((p) => ({
    player_key: p.member_id
      ? playerKeyFor(p.member_id)
      : playerKeyFor(undefined, p.name.replace(' (you)', '')),
    name: p.name.replace(' (you)', ''),
    isOrganizer: p.member_id === member.id,
  })), [roster, member.id]);

  const previewTeams = useMemo(() => {
    const keys = stakesRoster.map((p) => p.player_key);
    return buildTeamsFromSetup(
      roster.map((p) => ({ id: p.id, name: p.name })),
      keys,
      teamMode,
      manualTeams,
      autoTeams,
    );
  }, [stakesRoster, roster, teamMode, manualTeams]);

  useEffect(() => {
    const others = stakesRoster.filter((p) => p.player_key !== orgKey);
    if (!others.length) return;
    setMatchOpponents((prev) => {
      const next = { ...prev };
      if (enabled['match-play']) {
        if (!next['match-play'] || !others.some((p) => p.player_key === next['match-play'])) {
          next['match-play'] = others[0].player_key;
        }
      }
      return next;
    });
  }, [stakesRoster, orgKey, enabled['match-play']]);

  const wolfStakeConfig = stakes.wolf ?? stakes['lone-wolf'] ?? defaultStakeForFormat('wolf');
  const wolfShowMult = showWolfPointMultipliers(wolfStakeConfig);

  const startRound = async () => {
    if (!courseId) {
      setError('Pick a course');
      return;
    }
    if (totalPlayers > 4) {
      setError('Maximum 4 players per round');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const round = await db.groupRounds.create({
        course_id: courseId,
        tee_id: teeId,
        played_at: new Date(playedAt).toISOString(),
        holes,
      });

      const tee = (await db.courses.tees(courseId)).find((t) => t.id === teeId);
      const slope = tee?.slope ?? 130;

      const playerRows = roster.map((p) => {
        const isOrg = p.member_id === member.id;
        const m = members.find((x) => x.id === p.member_id);
        const guestIdx = 'guestIdx' in p ? (p as { guestIdx: number }).guestIdx : -1;
        const guestHcp = guestIdx >= 0 ? parseGuestHandicap(guests[guestIdx]?.handicap ?? '') : null;
        const hcp = isOrg ? member.handicap : m?.handicap ?? guestHcp;
        return {
          member_id: p.member_id || undefined,
          guest_name: p.member_id ? undefined : p.name,
          display_name: p.name.replace(' (you)', ''),
          handicap: hcp,
          course_handicap: courseHcpForPlayer(hcp, slope),
          tee_id: teeId,
          is_organizer: isOrg,
          team_index: null,
        };
      });

      await db.groupRounds.setPlayers(round.id, playerRows);

      const openToJoin = isJoinable && totalPlayers < 4;
      await db.groupRounds.update(round.id, {
        config: { is_joinable: openToJoin, game_mode: skipGames || !enabledIds.length ? 'casual' : 'games' },
      });

      if (!skipGames && enabledIds.length) {
        const orgKey = playerKeyFor(member.id);
        const keys = playerRows.map((p) => playerKeyFor(p.member_id, p.guest_name));
        let teams: string[][] | undefined;
        if (needsTeams) {
          teams = buildTeamsFromSetup(
            roster.map((p) => ({ id: p.id, name: p.name })),
            keys,
            teamMode,
            manualTeams,
            autoTeams,
          );
        }
        if (teams) {
          await db.groupRounds.update(round.id, { config: { is_joinable: openToJoin, game_mode: 'games', teams } });
        }
        const rows = stakesToGameRows(
          enabledIds,
          stakes,
          playerRows.map((p, i) => ({
            player_key: keys[i],
            member_id: p.member_id ?? null,
          })),
          orgKey,
          enabledIds.includes('wolf') || enabledIds.includes('lone-wolf') ? wolfConfig : undefined,
          matchOpponents,
          nassauMatches,
          teams,
        );
        await db.groupRounds.setGames(round.id, rows);
      }

      await db.groupRounds.start(round.id);
      onStarted(round.id);
    } catch (err) {
      setError((err as Error).message || 'Could not start round');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-8 pt-4">
      <div className="relative mb-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute left-0 top-0 inline-flex items-center gap-1 text-sm font-semibold text-ink-soft"
        >
          <ChevronLeft size={18} /> Back
        </button>
        <div className="px-14 pt-0.5 text-center">
          <div className="font-serif text-xl font-bold text-ink">Create Round</div>
          <p className="mt-1 text-xs text-ink-soft">
            {wizardStep === 'setup'
              ? 'Pick who\'s playing first — add games on the next step.'
              : wizardStep === 'games'
                ? 'Optional — pick games and stakes, or skip to scorecard only.'
                : 'Assign teams for team formats before starting.'}
          </p>
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
      {capMessage && <div className="mb-3 rounded-lg bg-gold/15 px-3 py-2 text-xs font-semibold text-navy">{capMessage}</div>}

      <div className="space-y-4">
        {wizardStep === 'setup' ? (
        <>
        <div>
          <label className="mb-2 block text-xs font-bold text-navy">WHO&apos;S PLAYING?</label>
          <GameSetupMemberSelect
            organizer={member}
            members={members}
            following={following}
            selectedIds={participants}
            onToggle={(id, adding) => {
              setParticipants((prev) => {
                if (!adding) return prev.filter((x) => x !== id);
                if (prev.includes(id) || atCap) return prev;
                return [...prev, id];
              });
            }}
            newPlayers={guests}
            onNewPlayersChange={setGuests}
            atCap={atCap}
            onCapHit={handleCapHit}
            onFollowingChange={setFollowing}
            organizerPlaying
            showGuestHandicap
          />
          <div className="mt-1 text-xs text-mute">{totalPlayers}/4 players</div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold text-navy">COURSE</label>
          <CourseTabPicker courses={courses} value={courseId} onChange={setCourseId} />
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold text-navy">HOLES</label>
          <RoundLengthPicker
            length={roundLength}
            nineSide={nineSide}
            onLengthChange={setRoundLength}
            onNineSideChange={setNineSide}
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold text-navy">DATE & TIME</label>
          <input
            type="datetime-local"
            value={playedAt}
            onChange={(e) => setPlayedAt(e.target.value)}
            className="w-full rounded-[10px] border border-line bg-paper px-3 py-2.5 text-sm"
          />
        </div>

        <OpenToJoinersCard
          active={isJoinable}
          disabled={atCap}
          onToggle={() => setIsJoinable((v) => !v)}
          description={
            atCap
              ? 'Your foursome is full — no open spots.'
              : isJoinable
                ? 'Visible on Play — members can tap Join while you have room.'
                : 'Invite-only — only players you add can join this round.'
          }
          hint={
            isJoinable && !atCap
              ? 'Open groups appear under Open Groups on the Play tab for other members.'
              : null
          }
        />
        </>
        ) : wizardStep === 'games' ? (
          <>
            <CategorizedGamePicker
              enabled={enabled}
              onToggle={(id) => setEnabled((prev) => ({ ...prev, [id]: !prev[id] }))}
              playerCount={totalPlayers}
            />
            {enabledIds.length > 0 && (
              <StakesScoringPanel
                enabledIds={enabledIds}
                stakes={stakes}
                onChange={setStakes}
                roster={stakesRoster}
                organizerKey={orgKey}
                opponentKeys={matchOpponents}
                onOpponentChange={(formatId, opponentKey) => {
                  setMatchOpponents((prev) => ({ ...prev, [formatId]: opponentKey }));
                }}
                nassauMatches={nassauMatches}
                onNassauMatchesChange={(formatId, matches) => {
                  setNassauMatches((prev) => ({ ...prev, [formatId]: matches }));
                }}
                teams={previewTeams}
              />
            )}
            {(enabled.wolf || enabled['lone-wolf']) && (
              <WolfSetupPanel
                config={wolfConfig}
                onChange={setWolfConfig}
                showPointMultipliers={wolfShowMult}
              />
            )}
            <label className="flex items-center gap-2 text-xs text-ink-soft">
              <input type="checkbox" checked={skipGames} onChange={(e) => setSkipGames(e.target.checked)} />
              Skip games — scorecard only
            </label>
          </>
        ) : (
          <RoundTeamsSetupPanel
            roster={roster.map((p) => ({ id: p.id, name: p.name }))}
            teamMode={teamMode}
            onTeamModeChange={setTeamMode}
            manualTeams={manualTeams}
            onManualTeamChange={(playerId, teamIndex) => {
              setManualTeams((prev) => ({ ...prev, [playerId]: teamIndex }));
            }}
          />
        )}
      </div>

      {wizardStep === 'setup' ? (
        <button
          type="button"
          disabled={totalPlayers > 4}
          onClick={() => setWizardStep('games')}
          className="mt-6 w-full rounded-xl bg-gold py-3.5 text-[15px] font-extrabold text-navy disabled:opacity-60"
        >
          Next: Games & Stakes
        </button>
      ) : wizardStep === 'games' ? (
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => setWizardStep('setup')}
            className="flex-1 rounded-xl border border-line py-3.5 text-sm font-bold text-navy"
          >
            Back
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              if (needsTeams) setWizardStep('teams');
              else void startRound();
            }}
            className="flex-[2] rounded-xl bg-gold py-3.5 text-[15px] font-extrabold text-navy disabled:opacity-60"
          >
            {needsTeams ? 'Next: Teams' : submitting ? 'Starting…' : 'Start Round'}
          </button>
        </div>
      ) : (
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => setWizardStep('games')}
            className="flex-1 rounded-xl border border-line py-3.5 text-sm font-bold text-navy"
          >
            Back
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void startRound()}
            className="flex-[2] rounded-xl bg-gold py-3.5 text-[15px] font-extrabold text-navy disabled:opacity-60"
          >
            {submitting ? 'Starting…' : 'Start Round'}
          </button>
        </div>
      )}
    </div>
  );
}
